import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR, enUS, es } from 'date-fns/locale';
import { useCaseEvents, CaseEvent, EventType } from '@/hooks/useCaseEvents';
import { useTranslation } from 'react-i18next';

interface CaseTimelineTabProps {
  caseId: string;
  caseCreatedAt?: string;
}

// Determine dot color: blue for biometrics/filing/documents, orange for others
const getDotColor = (category: string | undefined) => {
  switch (category) {
    case 'biometrics':
    case 'filing':
    case 'documents':
      return 'bg-blue-500';
    case 'approval':
      return 'bg-emerald-500';
    default:
      return 'bg-orange-500';
  }
};

export default function CaseTimelineTab({ caseId, caseCreatedAt }: CaseTimelineTabProps) {
  const { t, i18n } = useTranslation();
  const { events, eventTypes, isLoading, createEvent, deleteEvent, isCreating, isDeleting } = useCaseEvents(caseId);

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'pt-BR': return ptBR;
      case 'es': return es;
      default: return enUS;
    }
  };

  // Create virtual "Case Opened" event from case creation date
  // Sort: oldest first, then REVERSE so newest appears at TOP visually
  const allEvents = React.useMemo(() => {
    interface TimelineEvent extends CaseEvent {
      isVirtual?: boolean;
    }
    
    const virtualEvents: TimelineEvent[] = [];
    
    // Check if there's already a "Filing Submitted" event - if so, don't create virtual "Case Aberto"
    const hasFilingSubmitted = events.some(e => e.event_type?.code === 'filing_submitted');
    
    if (caseCreatedAt && !hasFilingSubmitted) {
      virtualEvents.push({
        id: 'case-created',
        case_id: caseId,
        event_type_id: 'virtual-case-opened',
        event_date: caseCreatedAt.split('T')[0],
        event_time: null,
        deadline_date: null,
        location_address: null,
        location_details: null,
        officer_id: null,
        officer_name: null,
        result: null,
        notes: null,
        additional_data: null,
        action_required: null,
        action_completed_at: null,
        client_notified_at: null,
        created_at: caseCreatedAt,
        created_by: null,
        event_type: { 
          id: 'virtual-case-opened', 
          name: t('caseTimeline.caseOpened'), 
          code: 'case_opened',
          category: 'filing',
          icon: '📂',
          color: null,
        },
        title: t('caseTimeline.caseOpened'),
        isVirtual: true,
      });
    }

    const combined: TimelineEvent[] = [...events, ...virtualEvents];
    // Sort ascending (oldest first), then reverse for display (newest at top, oldest at bottom)
    return combined
      .sort((a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime())
      .reverse();
  }, [events, caseCreatedAt, caseId]);

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({
    event_type_id: '',
    event_date: '',
    event_time: '',
    title: '',
    location_address: '',
    location_details: '',
    officer_name: '',
    result: '',
    notes: '',
  });

  const selectedEventType = eventTypes.find(et => et.id === formData.event_type_id);

  const handleSubmit = async () => {
    if (!formData.event_type_id || !formData.event_date) {
      toast.error(t('caseTimeline.eventTypeRequired'));
      return;
    }

    try {
      await createEvent({
        event_type_id: formData.event_type_id,
        event_date: formData.event_date,
        event_time: formData.event_time || undefined,
        title: formData.title || undefined,
        location_address: formData.location_address || undefined,
        location_details: formData.location_details || undefined,
        officer_name: formData.officer_name || undefined,
        result: formData.result || undefined,
        notes: formData.notes || undefined,
      });
      toast.success(t('caseTimeline.eventAddedSuccess'));
      setShowAddDialog(false);
      setFormData({
        event_type_id: '',
        event_date: '',
        event_time: '',
        title: '',
        location_address: '',
        location_details: '',
        officer_name: '',
        result: '',
        notes: '',
      });
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error(t('caseTimeline.eventAddFailed'));
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('caseTimeline.confirmDeleteEvent'))) return;
    
    try {
      await deleteEvent(id);
      toast.success(t('caseTimeline.eventDeleted'));
    } catch (error) {
      toast.error(t('caseTimeline.eventDeleteFailed'));
    }
  };

  const getCreatorDisplay = (event: CaseEvent & { isVirtual?: boolean }) => {
    if (event.isVirtual) return t('caseTimeline.system');
    if (event.creator) {
      return event.creator.display_name || 
        `${event.creator.first_name || ''} ${event.creator.last_name || ''}`.trim() || 
        t('caseTimeline.unknown');
    }
    return t('caseTimeline.unknown');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <Button onClick={() => setShowAddDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {t('caseTimeline.addActivity')}
        </Button>
      </div>

      {allEvents.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <p>{t('caseTimeline.noEventsRecorded')}</p>
          <p className="text-sm mt-2">{t('caseTimeline.noEventsHint')}</p>
        </div>
      ) : (
        <div className="relative pl-6">
          {/* Continuous vertical line */}
          <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-border" />
          
          <div className="space-y-6">
            {allEvents.map((event, index) => {
              const eventDate = new Date(event.event_date);
              const formattedDate = format(eventDate, "d 'de' MMMM 'de' yyyy", { locale: getDateLocale() });
              const formattedTime = event.event_time ? ` ${t('dates.at')} ${event.event_time}` : '';
              const location = event.location_address || (event.additional_data as any)?.address;
              const formType = (event.additional_data as any)?.form;
              
              // Check if this is a clock eligibility event
              const isClockEligible = event.event_type?.code === 'clock_eligible';
              const isPastDate = eventDate <= new Date();
              
              return (
                <div key={event.id} className="relative group">
                  {/* Timeline dot */}
                  <div className={`absolute -left-6 top-1 w-3.5 h-3.5 rounded-full ${getDotColor(event.event_type?.category)} ring-4 ring-background`} />
                  
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      {/* Date */}
                      <p className="text-sm text-muted-foreground mb-1">
                        {formattedDate}{formattedTime}
                      </p>
                      
                      {/* Event name with optional form badge */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold text-foreground">
                          {isClockEligible ? t('caseTimeline.eligibleForEAD') : (event.title || event.event_type?.name || t('caseTimeline.event'))}
                        </h4>
                        {isClockEligible && isPastDate && (
                          <Badge variant="default" className="text-xs bg-emerald-500 hover:bg-emerald-600">
                            {t('caseTimeline.eligible')}
                          </Badge>
                        )}
                        {formType && !isClockEligible && (
                          <Badge variant="outline" className="text-xs">
                            {formType}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Location if exists */}
                      {location && (
                        <p className="text-sm text-muted-foreground mt-1 truncate max-w-md">
                          {location}
                        </p>
                      )}
                      
                      {/* Notes if exists */}
                      {event.notes && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {event.notes}
                        </p>
                      )}
                      
                      {/* Added by */}
                      <p className="text-xs text-muted-foreground/70 mt-2">
                        {t('caseTimeline.addedBy', { name: getCreatorDisplay(event) })}
                      </p>
                    </div>
                    
                    {/* Delete button (hidden for virtual events) */}
                    {!(event as any).isVirtual && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDelete(event.id)}
                        disabled={isDeleting}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add Event Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('caseTimeline.addActivity')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>{t('caseTimeline.activityType')} *</Label>
              <Select
                value={formData.event_type_id}
                onValueChange={(value) => setFormData({ ...formData, event_type_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('caseTimeline.selectActivityType')} />
                </SelectTrigger>
                <SelectContent>
                  {eventTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('caseTimeline.date')} *</Label>
                <Input
                  type="date"
                  value={formData.event_date}
                  onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                />
              </div>
              <div>
                <Label>{t('caseTimeline.time')}</Label>
                <Input
                  type="time"
                  value={formData.event_time}
                  onChange={(e) => setFormData({ ...formData, event_time: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>{t('caseTimeline.title')}</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={t('caseTimeline.optionalTitle')}
              />
            </div>

            {selectedEventType?.requires_location && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('caseTimeline.locationAddress')}</Label>
                  <Input
                    value={formData.location_address}
                    onChange={(e) => setFormData({ ...formData, location_address: e.target.value })}
                    placeholder={t('caseTimeline.address')}
                  />
                </div>
                <div>
                  <Label>{t('caseTimeline.locationDetails')}</Label>
                  <Input
                    value={formData.location_details}
                    onChange={(e) => setFormData({ ...formData, location_details: e.target.value })}
                    placeholder={t('caseTimeline.roomFloor')}
                  />
                </div>
              </div>
            )}

            {selectedEventType?.requires_officer && (
              <div>
                <Label>{t('caseTimeline.officerName')}</Label>
                <Input
                  value={formData.officer_name}
                  onChange={(e) => setFormData({ ...formData, officer_name: e.target.value })}
                  placeholder={t('caseTimeline.officerName')}
                />
              </div>
            )}

            {selectedEventType?.requires_result && (
              <div>
                <Label>{t('caseTimeline.result')}</Label>
                <Input
                  value={formData.result}
                  onChange={(e) => setFormData({ ...formData, result: e.target.value })}
                  placeholder={t('caseTimeline.result')}
                />
              </div>
            )}

            <div>
              <Label>{t('caseTimeline.notes')}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder={t('caseTimeline.additionalNotes')}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              {t('caseTimeline.cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={isCreating}>
              {isCreating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('caseTimeline.adding')}
                </>
              ) : (
                t('caseTimeline.addActivity')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
